// Şifreli yedek ve kurtarma kodu (9.34 · D-600).
//
// NEDEN AYRI BİR ŞİFRELEME: Signal Protokolü'nün şifreli metni arşivlenemez.
// Ratchet her mesaj anahtarını kullandıktan sonra siler — ileriye dönük
// gizliliğin kendisi budur — yani sunucudaki taşıma şifreli metni sonradan
// KULLANICININ KENDİSİ için bile açılamaz. Geçmişi taşımanın tek yolu, yerel düz
// metni AYRI bir anahtarla şifreleyip saklamaktır. Taşıma katmanı ve yedek
// katmanı birbirinden bağımsızdır.
//
// NEDEN PIN DEĞİL KOD: yedek anahtarı yalnız kısa bir PIN'den türetilseydi,
// blob'u elinde tutan herkes — BİZ DAHİL — PIN'i deneyerek kırardı. Kısa PIN'i
// güvenli kılan şey, denemeyi sınırlayan ve anahtarı silen bir donanım kasasıdır;
// öyle bir altyapımız yok. Yüksek entropili kurtarma kodu, kasa olmadan
// garantiyi koruyan tek yol (D-600'ün "donanım kasası gelecek" notu geçerli).
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { scrypt } from '@noble/hashes/scrypt.js';
import { randomBytes } from '@noble/hashes/utils.js';
import { xchacha20poly1305 } from '@noble/ciphers/chacha.js';
import { bytesToBase64, base64ToBytes, bytesToUtf8, utf8ToBytes } from './crypto';
import { api } from '../services/api';
import { exportPlaintext, importPlaintext } from './plaintextCache';

// Karışması kolay harf/rakam yok (0/O, 1/I/L). 32 sembol = karakter başına 5 bit;
// 32 karakter = 160 bit. Elle yazılabilsin diye dörtlü gruplanıyor.
const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789#';
const CODE_CHARS = 32;
const GROUP = 4;

// scrypt parametreleri: kod zaten yüksek entropili olduğu için kriptografik
// zorunluluk değil, kullanıcı kodu zayıflatırsa diye ucuz bir sigorta. Telefonda
// kabul edilebilir süre.
const SCRYPT = { N: 2 ** 14, r: 8, p: 1, dkLen: 32 } as const;

/** Yeni kurtarma kodu: "ABCD-EFGH-..." biçiminde, 160 bit. */
export function generateRecoveryCode(): string {
  const bytes = randomBytes(CODE_CHARS);
  let code = '';
  for (let i = 0; i < CODE_CHARS; i++) {
    code += ALPHABET[bytes[i] % ALPHABET.length];
  }
  return code.match(new RegExp(`.{1,${GROUP}}`, 'g'))!.join('-');
}

/** Girilen kodu karşılaştırma/türetme için sadeleştir: ayraç ve büyük/küçük fark etmez. */
export function normalizeRecoveryCode(code: string): string {
  return code.toUpperCase().replace(/[^A-Z0-9#]/g, '');
}

function deriveKey(code: string, salt: Uint8Array): Uint8Array {
  const stretched = scrypt(utf8ToBytes(normalizeRecoveryCode(code)), salt, SCRYPT);
  // HKDF ile alan ayrımı: aynı koddan ileride başka amaçla anahtar türetilirse
  // birbirine karışmasın.
  return hkdf(sha256, stretched, salt, utf8ToBytes('nosubject-backup-1'), 32);
}

export type BackupBlob = {
  v: 1;
  salt: string;
  nonce: string;
  data: string;
};

/**
 * Geçmişi kurtarma koduyla şifrele. Sunucuya yalnız bu blob gider; içindeki
 * hiçbir şey bizim için anlamlı değildir.
 */
export function sealBackup(entries: Record<string, string>, code: string): BackupBlob {
  const salt = randomBytes(16);
  const nonce = randomBytes(24);
  const key = deriveKey(code, salt);
  const plaintext = utf8ToBytes(JSON.stringify(entries));
  return {
    v: 1,
    salt: bytesToBase64(salt),
    nonce: bytesToBase64(nonce),
    data: bytesToBase64(xchacha20poly1305(key, nonce).encrypt(plaintext)),
  };
}

/**
 * Yedeği aç. Yanlış kodda AEAD doğrulaması düşer ve `null` döner — "yanlış kod"
 * ile "bozuk yedek" arayüz için aynı sonuçtur: geçmiş açılamadı.
 */
export function openBackup(
  // Girdi sunucudan geliyor, yani sürümü HENÜZ bilinmiyor: `v` burada geniş
  // tutulup aşağıda denetleniyor. Dar tip, gelen veriyi doğrulanmış saymak olurdu.
  blob: { v: number; salt: string; nonce: string; data: string },
  code: string,
): Record<string, string> | null {
  if (!blob || blob.v !== 1) return null;
  try {
    const key = deriveKey(code, base64ToBytes(blob.salt));
    const plaintext = xchacha20poly1305(key, base64ToBytes(blob.nonce))
      .decrypt(base64ToBytes(blob.data));
    const parsed = JSON.parse(bytesToUtf8(plaintext));
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    return parsed as Record<string, string>;
  } catch {
    return null;
  }
}

// ---- Orkestrasyon: yerel geçmiş ↔ sunucudaki blob (9.34 · D-600) ----

/**
 * Yeni bir kurtarma kodu üret, geçmişi onunla şifrele, sunucuya yükle ve KODU
 * DÖNDÜR. Kod hiçbir yere kaydedilmez — ne bizde ne cihazda. Kullanıcı saklamazsa
 * geri dönüşü yoktur; garantinin bedeli tam olarak budur.
 */
export async function createBackup(token: string): Promise<{ code: string; messages: number }> {
  const entries = await exportPlaintext();
  const code = generateRecoveryCode();
  await api.putBackup(token, sealBackup(entries, code));
  return { code, messages: Object.keys(entries).length };
}

/** Var olan yedeği aynı kodla tazele (yeni mesajlar eklendikçe). */
export async function refreshBackup(token: string, code: string): Promise<number> {
  const entries = await exportPlaintext();
  await api.putBackup(token, sealBackup(entries, code));
  return Object.keys(entries).length;
}

/**
 * Yedeği indir, kodla aç ve geçmişi önbelleğe yaz. Kod yanlışsa ya da yedek
 * yoksa `null` döner — arayüz ikisini de "geçmiş açılamadı" diye gösterir.
 */
export async function restoreBackup(token: string, code: string): Promise<number | null> {
  let blob;
  try {
    blob = await api.getBackup(token);
  } catch {
    return null;
  }
  const entries = openBackup(blob, code);
  if (!entries) return null;
  return importPlaintext(entries);
}
